import { safeFetch, getBaseUrl } from './api';

// All IPD routes: /ipd/admissions/:admissionId/...
const url = (path: string) => `${getBaseUrl()}${path}`;

// ─── Types ───────────────────────────────────────────────────────────────────

export interface IpdVital {
  id: string;
  admission_id: string;
  recorded_at: string;
  bp_systolic: number | null;
  bp_diastolic: number | null;
  pulse: number | null;
  temp_celsius: number | null;
  spo2: number | null;
  respiratory_rate: number | null;
  weight_kg: number | null;
  urine_output_ml: number | null;
  fluid_intake_ml: number | null;
  notes: string | null;
  recorded_by: string;
  recorded_by_name: string;
  created_at: string;
}

export interface AddVitalPayload {
  recorded_at?: string;
  bp_systolic?: number | null;
  bp_diastolic?: number | null;
  pulse?: number | null;
  temp_celsius?: number | null;
  spo2?: number | null;
  respiratory_rate?: number | null;
  weight_kg?: number | null;
  urine_output_ml?: number | null;
  fluid_intake_ml?: number | null;
  notes?: string;
}

// ─── Discharge Types ─────────────────────────────────────────────────────────

export interface DischargeMedicine {
  id?: string;
  drug_id?: number | null;
  drug_name: string;
  drug_type?: string;
  dose?: string;
  taken_when?: string;
  frequency?: string;
  duration?: string;
  notes?: string;
  sort_order?: number;
}

export interface DischargeSummary {
  id: string;
  admission_id: string;
  final_diagnosis: string | null;
  complaints: string | null;
  key_findings_physical: string | null;
  investigation_summary: string | null;
  procedures_done: string | null;
  operative_findings: string | null;
  hospital_course: string | null;
  treatment_given: string | null;
  condition_at_discharge: 'improved' | 'stable' | 'same' | 'deteriorated' | 'expired';
  discharge_advice: string | null;
  additional_medication: string | null;
  others: string | null;
  tests_requested: string | null;
  follow_up_date: string | null;
  follow_up_notes: string | null;
  diet_advice: string | null;
  activity_restriction: string | null;
  acknowledgement: string | null;
  finalized_at: string | null;
  finalized_by: string | null;
  finalized_by_name?: string | null;
  /** Last reopen of a finalized summary for amendment */
  reopened_at?: string | null;
  reopened_by_name?: string | null;
  reopen_reason?: string | null;
  created_at: string;
  updated_at: string;
}

export interface DischargePayload extends Omit<DischargeSummary, 'id' | 'admission_id' | 'finalized_at' | 'finalized_by' | 'finalized_by_name' | 'reopened_at' | 'reopened_by_name' | 'reopen_reason' | 'created_at' | 'updated_at'> {
  patient_history?: string;
  medicines: DischargeMedicine[];
  finalize?: boolean;
}

export interface DischargeData {
  admission: any;
  summary: DischargeSummary | null;
  medicines: DischargeMedicine[];
  activeOrders: IpdMedicationOrder[];
}

export interface IpdProgressNote {
  id: string;
  admission_id: string;
  note_text: string;
  noted_at: string;
  noted_by: string;
  noted_by_name: string;
  edited_by: string | null;
  edited_by_name: string | null;
  edited_at: string | null;
  created_at: string;
  /** Earlier wordings, oldest first (only for amended notes) */
  versions?: { note_text: string; written_by_name: string | null; written_at: string | null; replaced_at: string }[];
}

export interface AddNotePayload {
  note_text: string;
  noted_at?: string;
}

export interface IpdMedicationOrder {
  id: string;
  admission_id: string;
  drug_id: number | null;
  drug_name: string;
  generic_name: string | null;
  dose: string;
  route: 'oral' | 'iv' | 'im' | 'sc' | 'topical' | 'inhalation' | 'other';
  frequency: string;
  start_date: string;
  end_date: string | null;
  is_prn: number;
  prn_reason: string | null;
  special_instructions: string | null;
  status: 'active' | 'stopped' | 'completed';
  /** Set when the hospital doesn't bill this medicine (patient's own, outside pharmacy) */
  not_billed_reason?: string | null;
  ordered_by: string;
  ordered_by_name: string;
  stopped_by: string | null;
  stopped_by_name: string | null;
  stopped_at: string | null;
  stop_reason: string | null;
  created_at: string;
}

export interface CreateOrderPayload {
  drug_id?: number | null;
  drug_name: string;
  generic_name?: string;
  dose: string;
  route: string;
  frequency: string;
  start_date: string;
  end_date?: string | null;
  is_prn?: boolean;
  prn_reason?: string;
  special_instructions?: string;
  not_billed_reason?: string | null;
}

// ─── Vitals API ───────────────────────────────────────────────────────────────

export async function getVitals(admissionId: string): Promise<IpdVital[]> {
  return safeFetch<IpdVital[]>(url(`/ipd/admissions/${admissionId}/vitals`));
}

export async function getLatestVital(admissionId: string): Promise<IpdVital | null> {
  return safeFetch<IpdVital | null>(url(`/ipd/admissions/${admissionId}/vitals/latest`));
}

export async function addVital(admissionId: string, data: AddVitalPayload): Promise<IpdVital> {
  return safeFetch<IpdVital>(url(`/ipd/admissions/${admissionId}/vitals`), {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ─── Ward & Bed API ───────────────────────────────────────────────────────────

export type DailyChargeType = 'nursing' | 'consultation' | 'diet' | 'other';
/** A charge per night of the stay besides the bed (nursing, duty doctor/RMO, diet …) */
export interface WardDailyCharge { name: string; charge_type: DailyChargeType; amount: number }

export interface Ward {
  id: string; name: string; ward_type: string;
  default_rent_per_day: number;
  is_active: number; notes: string | null;
  bed_count: number; available_beds: number; occupied_beds: number;
  daily_charges?: WardDailyCharge[];
}
export interface Bed {
  id: string; ward_id: string; bed_number: string;
  rent_per_day: number | null; status: string; is_active: number;
  notes: string | null;
  current_admission_id: string | null;
  admission_number: string | null;
  current_patient_name: string | null;
  occupancy_status: string;
  effective_rent_per_day: number | null;  // from bed_occupancy view: bed.rent_per_day ?? ward.default_rent_per_day
}

export async function getWards(clinicId: string): Promise<Ward[]> {
  return safeFetch(url(`/ipd/wards?clinic_id=${clinicId}`));
}
export async function createWard(data: Partial<Ward>): Promise<Ward> {
  return safeFetch(url('/ipd/wards'), { method: 'POST', body: JSON.stringify(data) });
}
export async function updateWard(wardId: string, data: Partial<Ward>): Promise<Ward> {
  return safeFetch(url(`/ipd/wards/${wardId}`), { method: 'PATCH', body: JSON.stringify(data) });
}
/** Replace a ward's daily charges (new admissions and transfers into the ward use them) */
export async function setWardDailyCharges(
  wardId: string, charges: { name: string; charge_type: DailyChargeType; amount_per_day: number }[]
): Promise<WardDailyCharge[]> {
  return safeFetch(url(`/ipd/wards/${wardId}/daily-charges`), { method: 'PUT', body: JSON.stringify({ charges }) });
}
export async function getBeds(wardId: string): Promise<Bed[]> {
  return safeFetch(url(`/ipd/wards/${wardId}/beds`));
}
export async function createBed(wardId: string, data: Partial<Bed>): Promise<Bed> {
  return safeFetch(url(`/ipd/wards/${wardId}/beds`), { method: 'POST', body: JSON.stringify(data) });
}
export async function updateBed(bedId: string, data: Partial<Bed>): Promise<Bed> {
  return safeFetch(url(`/ipd/beds/${bedId}`), { method: 'PATCH', body: JSON.stringify(data) });
}

// ─── Admit / Charges / Invoice API ───────────────────────────────────────────

export async function createAdmission(data: any): Promise<{ id: string; admission_number: string; deposit_error: string | null }> {
  return safeFetch(url('/ipd/admissions'), { method: 'POST', body: JSON.stringify(data) });
}
export async function addCharge(admissionId: string, data: any): Promise<any> {
  return safeFetch(url(`/ipd/admissions/${admissionId}/charges`), { method: 'POST', body: JSON.stringify(data) });
}
// ─── Bill API ────────────────────────────────────────────────────────────────

/** A line worked out from the stay: the bed, or one of the ward's daily charges */
export interface BedChargeLine {
  kind: 'bed' | 'daily';
  /** Bill category: room_rent for the bed, else the daily charge's type */
  category: string;
  /** "Bed charges", or the daily charge's name */
  label: string;
  description: string;
  ward_name: string;
  bed_number: string;
  from_date: string;
  to_date: string;
  days: number;
  rate: number;
  total: number;
}

export interface AdmissionDeposit {
  id: string;
  amount: string;
  receipt_number?: string | null;
  payment_mode: string;
  reference_number: string | null;
  notes: string | null;
  created_at: string;
  collected_by_name: string | null;
}

export interface AdmissionPayment {
  id: string;
  amount: string;
  payment_mode: string;
  reference_number: string | null;
  status: 'success' | 'failed' | 'refunded';
  payment_date: string;
  collected_by_name: string | null;
}

export interface AdmissionRefund {
  id: string;
  amount: string;
  receipt_number?: string | null;
  refund_mode: string;
  reason: string | null;
  created_at: string;
  processed_by_name: string | null;
}

/** The admission's money: live while the bill is open, the invoice's once final. */
export interface AdmissionAccount {
  bill_status: 'open' | 'final';
  bed_lines: BedChargeLine[];
  charges_total: number;
  bed_total: number;
  gross_total: number;
  discount_amount: number;
  discount_reason: string | null;
  bill_total: number;
  paid: number;
  balance_due: number;
  advance_available: number;
  /** Still to collect after the advance held */
  to_collect: number;
  /** Advance left over after the bill */
  spare_advance: number;
  /** Final bill no longer matches the stay */
  out_of_date: boolean;
  live_total: number;
  invoice: { id: string; invoice_number: string } | null;
  finalized_at: string | null;
  finalized_by_name: string | null;
  /** Deposit used on the bill, net of any put back after a reopen */
  deposit_used: number;
  deposits: AdmissionDeposit[];
  payments: AdmissionPayment[];
  refunds: AdmissionRefund[];
  /** Medicines ordered that are neither on the bill nor marked as not billed here */
  unbilled_orders: Pick<IpdMedicationOrder, 'id' | 'drug_name' | 'dose' | 'route' | 'frequency' | 'start_date' | 'end_date' | 'status'>[];
}

export async function getAccount(admissionId: string, until?: string): Promise<AdmissionAccount> {
  const q = until ? `?until=${encodeURIComponent(until)}` : '';
  return safeFetch(url(`/ipd/admissions/${admissionId}/account${q}`));
}

export async function collectDeposit(
  admissionId: string,
  data: { amount: number; payment_mode: string; reference_number?: string; upi_app?: string; notes?: string }
): Promise<{ available_advance: number; deposit?: { receipt_number?: string | null } }> {
  return safeFetch(url(`/ipd/admissions/${admissionId}/deposits`), { method: 'POST', body: JSON.stringify(data) });
}

export async function finalizeBill(
  admissionId: string,
  data: { discount_amount?: number; discount_type?: 'flat' | 'percent'; discount_reason?: string; use_advance?: boolean }
): Promise<{ invoice_id: string; invoice_number: string; total: number }> {
  return safeFetch(url(`/ipd/admissions/${admissionId}/bill/finalize`), { method: 'POST', body: JSON.stringify(data) });
}

export async function reopenBill(admissionId: string, reason: string): Promise<{ id: string; bill_status: string }> {
  return safeFetch(url(`/ipd/admissions/${admissionId}/bill/reopen`), { method: 'POST', body: JSON.stringify({ reason }) });
}

// ─── Admission API ───────────────────────────────────────────────────────────

export interface AdmissionRow {
  id: string;
  admission_number: string;
  status: string;
  ward_id: string;
  admission_date: string;
  discharge_date: string | null;
  patient_name: string;
  patient_mrn: string;
  admitting_doctor_name: string;
  ward_name: string;
  bed_number: string;
  bill_status: 'open' | 'final';
  discharge_advised_at: string | null;
  is_mlc: number;
  invoice_number: string | null;
  total_amount: number | null;
  paid_amount: number | null;
  balance_due: number | null;
  available_advance: number | null;
}

export async function getAdmissions(
  clinicId: string,
  opts?: { status?: 'admitted' | 'all'; patientId?: string; dateFrom?: string; dateTo?: string; search?: string }
): Promise<AdmissionRow[]> {
  const params = new URLSearchParams({ clinic_id: clinicId });
  if (opts?.status)    params.set('status', opts.status);
  if (opts?.patientId) params.set('patient_id', opts.patientId);
  if (opts?.dateFrom)  params.set('date_from', opts.dateFrom);
  if (opts?.dateTo)    params.set('date_to', opts.dateTo);
  if (opts?.search)    params.set('search', opts.search);
  return safeFetch<AdmissionRow[]>(url(`/ipd/admissions?${params.toString()}`));
}

export async function getAdmission(admissionId: string): Promise<any> {
  return safeFetch(url(`/ipd/admissions/${admissionId}`));
}

export async function getCharges(admissionId: string, includeCancelled?: boolean): Promise<any[]> {
  const q = includeCancelled ? '?include_cancelled=true' : '';
  return safeFetch(url(`/ipd/admissions/${admissionId}/charges${q}`));
}

export async function cancelCharge(admissionId: string, chargeId: string, cancelReason: string): Promise<any> {
  return safeFetch(url(`/ipd/admissions/${admissionId}/charges/${chargeId}/cancel`), {
    method: 'PATCH',
    body: JSON.stringify({ cancel_reason: cancelReason }),
  });
}

// ─── Ward / bed transfer API ─────────────────────────────────────────────────

export interface TransferRecord {
  id: string;
  notes: string | null;
  transferred_at: string;
  from_ward_name: string;
  from_bed_number: string;
  to_ward_name: string;
  to_bed_number: string;
  transferred_by_name: string;
}

export async function transferAdmission(
  admissionId: string,
  data: { ward_id: string; bed_id: string; notes?: string }
): Promise<{ id: string; ward_id: string; bed_id: string }> {
  return safeFetch(url(`/ipd/admissions/${admissionId}/transfer`), { method: 'POST', body: JSON.stringify(data) });
}

export async function getTransferHistory(admissionId: string): Promise<TransferRecord[]> {
  return safeFetch(url(`/ipd/admissions/${admissionId}/transfers`));
}

// ─── Admission status API ────────────────────────────────────────────────────

export async function dischargeAdmission(
  admissionId: string,
  data: { status: string; discharge_date: string; billing_note?: string; death_cause?: string; lama_signed_by?: string }
): Promise<{ id: string; status: string; discharge_date: string }> {
  return safeFetch(url(`/ipd/admissions/${admissionId}/discharge`), { method: 'PATCH', body: JSON.stringify(data) });
}

export interface DischargeIssue { code: 'bill_not_final' | 'money_due' | 'no_summary' | 'not_advised'; text: string }

/** The bill as it will stand at that discharge time, and what is still open for that status */
export async function getDischargeCheck(admissionId: string, status: string, at: string): Promise<{ account: AdmissionAccount; issues: DischargeIssue[] }> {
  const q = new URLSearchParams({ status, at });
  return safeFetch(url(`/ipd/admissions/${admissionId}/discharge-check?${q.toString()}`));
}

/** A doctor advises discharge (advise=false withdraws it) */
export async function adviseDischarge(admissionId: string, advise: boolean): Promise<{ id: string; discharge_advised: boolean }> {
  return safeFetch(url(`/ipd/admissions/${admissionId}/discharge-advice`), { method: 'POST', body: JSON.stringify({ advise }) });
}

export interface AdmissionDetails {
  admission_reason: string;
  attendant_name?: string | null;
  attendant_relation?: string | null;
  attendant_phone?: string | null;
  is_mlc?: boolean;
  mlc_number?: string | null;
  referred_by?: string | null;
  rate_plan?: string | null;
  insurance_provider?: string | null;
  notes?: string | null;
}

export async function updateAdmissionDetails(admissionId: string, data: AdmissionDetails): Promise<AdmissionDetails> {
  return safeFetch(url(`/ipd/admissions/${admissionId}/details`), { method: 'PATCH', body: JSON.stringify(data) });
}

/** Mark orders as not billed here (reason), or back to be billed (null) */
export async function setOrdersBilling(admissionId: string, orderIds: string[], notBilledReason: string | null): Promise<{ updated: number }> {
  return safeFetch(url(`/ipd/admissions/${admissionId}/orders/billing`), {
    method: 'PATCH', body: JSON.stringify({ order_ids: orderIds, not_billed_reason: notBilledReason }),
  });
}

export async function updateAdmissionDates(
  admissionId: string,
  data: { admission_date: string; discharge_date?: string | null }
): Promise<{ id: string; admission_date: string; discharge_date: string | null }> {
  return safeFetch(url(`/ipd/admissions/${admissionId}/dates`), { method: 'PATCH', body: JSON.stringify(data) });
}

// ─── Discharge Summary API ───────────────────────────────────────────────────

export async function getDischarge(admissionId: string): Promise<DischargeData> {
  return safeFetch<DischargeData>(url(`/ipd/admissions/${admissionId}/discharge`));
}

export async function saveDischarge(admissionId: string, data: DischargePayload): Promise<{ summary: DischargeSummary; medicines: DischargeMedicine[] }> {
  return safeFetch(url(`/ipd/admissions/${admissionId}/discharge`), {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/** Unlock a finalized summary for amendment (reason required) */
export async function reopenDischarge(admissionId: string, reason: string): Promise<{ id: string }> {
  return safeFetch(url(`/ipd/admissions/${admissionId}/discharge/reopen`), {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

// ─── Progress Notes API ───────────────────────────────────────────────────────

export async function getNotes(admissionId: string, date?: string): Promise<IpdProgressNote[]> {
  const q = date ? `?date=${date}` : '';
  return safeFetch<IpdProgressNote[]>(url(`/ipd/admissions/${admissionId}/notes${q}`));
}

export async function addNote(admissionId: string, data: AddNotePayload): Promise<IpdProgressNote> {
  return safeFetch<IpdProgressNote>(url(`/ipd/admissions/${admissionId}/notes`), {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateNote(admissionId: string, noteId: string, noteText: string): Promise<IpdProgressNote> {
  return safeFetch<IpdProgressNote>(url(`/ipd/admissions/${admissionId}/notes/${noteId}`), {
    method: 'PATCH',
    body: JSON.stringify({ note_text: noteText }),
  });
}

// ─── Medication Orders API ────────────────────────────────────────────────────

export async function getOrders(admissionId: string, statusFilter?: 'all'): Promise<IpdMedicationOrder[]> {
  const q = statusFilter === 'all' ? '?status=all' : '';
  return safeFetch<IpdMedicationOrder[]>(url(`/ipd/admissions/${admissionId}/orders${q}`));
}

export async function createOrder(admissionId: string, data: CreateOrderPayload): Promise<IpdMedicationOrder> {
  return safeFetch<IpdMedicationOrder>(url(`/ipd/admissions/${admissionId}/orders`), {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function stopOrder(admissionId: string, orderId: string, stopReason: string): Promise<IpdMedicationOrder> {
  return safeFetch<IpdMedicationOrder>(url(`/ipd/admissions/${admissionId}/orders/${orderId}/stop`), {
    method: 'PATCH',
    body: JSON.stringify({ stop_reason: stopReason }),
  });
}
